/*
 * A full-width chart, for looking at one instrument properly.
 *
 * The instrument and the interval are kept in the URL query, so a particular
 * chart can be bookmarked or sent to somebody, and a reload comes back to the
 * same place rather than to a blank picker.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useCandles, useSeedQuotes, useStatus, useStrategies } from "../api/hooks";
import { useLive, useWatch } from "../api/stream";
import type { Interval } from "../api/types";
import { CandleChart } from "../charts/CandleChart";
import { IconWarning } from "../components/Icons";
import { LiveChange, LivePrice } from "../components/LivePrice";
import { SymbolPicker } from "../components/SymbolPicker";
import {
  DAYS_FOR,
  INTERVALS,
  compactNumber,
  price as fmtPrice,
} from "../lib/format";

export function ChartsPage() {
  const [params, setParams] = useSearchParams();
  const status = useStatus();
  const strategies = useStrategies();
  const live = useLive();

  const token = Number(params.get("token")) || null;
  const symbol = params.get("symbol") ?? "";
  const interval = (params.get("interval") as Interval) || "5minute";

  const [picking, setPicking] = useState(false);

  /* Everything already being watched, offered as quick chips — usually the
     thing you want to look at is something a strategy is already trading. */
  const known = useMemo(() => {
    const seen = new Map<number, string>();
    for (const s of strategies.data ?? []) {
      for (const w of s.watches) seen.set(w.instrumentToken, w.tradingsymbol);
    }
    return [...seen.entries()].map(([t, sym]) => ({ token: t, symbol: sym }));
  }, [strategies.data]);

  useEffect(() => {
    if (!token && known.length > 0) {
      setParams({ token: String(known[0].token), symbol: known[0].symbol, interval }, { replace: true });
    }
  }, [token, known, interval, setParams]);

  useWatch(token ? [token] : []);
  useSeedQuotes(token ? [token] : [], !!status.data?.sessionLive);

  const candles = useCandles(token ?? undefined, interval, DAYS_FOR[interval]);
  const quote = token ? live.quote(token) : undefined;

  const choose = (t: number, sym: string) =>
    setParams({ token: String(t), symbol: sym, interval });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{symbol || "Charts"}</h1>
          <p>
            {token ? (
              <span className="row tight">
                <LivePrice token={token} />
                <LiveChange token={token} />
                {quote?.volume ? (
                  <span className="faint small">Vol {compactNumber(quote.volume)}</span>
                ) : null}
              </span>
            ) : (
              "Pick an instrument to chart it."
            )}
          </p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => setPicking((p) => !p)}>
            {picking ? "Close search" : "Find an instrument"}
          </button>
        </div>
      </div>

      {picking && (
        <div className="card mb">
          <div className="card-body">
            <SymbolPicker
              autoFocus
              onPick={(i) => {
                choose(i.instrumentToken, i.tradingsymbol);
                setPicking(false);
              }}
            />
          </div>
        </div>
      )}

      {!status.data?.sessionLive && (
        <div className="banner warn">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>No Zerodha session for today</strong>
            <p>Candles and live prices both come from Zerodha, so neither will load.</p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="chart-toolbar">
          {known.length > 0 && (
            <div className="chip-group">
              {known.slice(0, 10).map((k) => (
                <button
                  key={k.token}
                  className={token === k.token ? "on" : ""}
                  onClick={() => choose(k.token, k.symbol)}
                >
                  {k.symbol}
                </button>
              ))}
            </div>
          )}
          <div className="topbar-spacer" />
          <div className="chip-group">
            {INTERVALS.map((i) => (
              <button
                key={i.value}
                className={interval === i.value ? "on" : ""}
                onClick={() =>
                  setParams({
                    token: String(token ?? ""),
                    symbol,
                    interval: i.value,
                  })
                }
              >
                {i.label.replace(" minutes", "m").replace(" minute", "m").replace("1 hour", "1h").replace("1 day", "1D")}
              </button>
            ))}
          </div>
        </div>

        {!token ? (
          <div className="empty" style={{ height: 460 }}>
            <strong>Nothing chosen</strong>
            Search for an instrument above.
          </div>
        ) : candles.isPending ? (
          <div className="empty" style={{ height: 460 }}><span className="spinner" /></div>
        ) : candles.isError ? (
          <div className="empty" style={{ height: 460 }}>
            <strong>No candles</strong>
            {String((candles.error as Error).message)}
          </div>
        ) : (
          <CandleChart
            token={token}
            bars={candles.data.bars}
            interval={interval}
            height={520}
          />
        )}
      </div>

      {quote?.depth && (
        <div className="grid cols-2 mt">
          <DepthTable title="Bids" rows={quote.depth.buy} tone="up" />
          <DepthTable title="Offers" rows={quote.depth.sell} tone="down" />
        </div>
      )}
    </>
  );
}

/** Five levels a side, straight off the full-mode tick packet. */
function DepthTable({
  title,
  rows,
  tone,
}: {
  title: string;
  rows: { qty: number; price: number; orders: number }[];
  tone: "up" | "down";
}) {
  return (
    <div className="card">
      <div className="card-head"><h2>{title}</h2></div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th className="num">Price</th>
              <th className="num">Quantity</th>
              <th className="num">Orders</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className={`num ${tone}`}>{fmtPrice(r.price)}</td>
                <td className="num">{r.qty.toLocaleString("en-IN")}</td>
                <td className="num faint">{r.orders}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
